// The global search box: typeahead for solicitation numbers (which open the notice), filters, titles
// and saved searches; empty, it offers recent searches. Arrow keys move, Enter chooses, Escape closes.
import { Fragment, useEffect, useRef, useState, type ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { api } from "../api/client";
import { FACET_NAMES, recentSearches, remember, searchPath, type SuggestNotice, type SuggestResponse } from "../api/search";
import { useUI } from "../app/context";
import { dueText } from "../lib/format";
import { Badge } from "./Badge";

type Option =
  | { kind: "query"; q: string; label: string; icon: ReactNode; sub?: string; hint?: string; group?: string }
  | { kind: "notice"; key: string; label: string; icon: ReactNode; sub?: string; group?: string }
  | { kind: "saved"; id: number; label: string; icon: ReactNode; sub?: string; group?: string }
  | { kind: "advanced"; label: string; icon: ReactNode; sub?: string; group?: string };

export function GlobalSearch({ value = "", autoFocus }: { value?: string; autoFocus?: boolean }) {
  const [text, setText] = useState(value);
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<Option[]>([]);
  const [active, setActive] = useState(-1);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { openPanel } = useUI();
  const timer = useRef<number | undefined>(undefined);
  const seq = useRef(0);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => setText(value), [value]);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  useEffect(() => {
    if (autoFocus) input.current?.focus();
  }, [autoFocus]);

  const close = () => {
    setOpen(false);
    setActive(-1);
  };
  const go = (q: string, sort?: string | null) => {
    remember(q);
    navigate(searchPath(q, sort));
  };
  const choose = (o: Option | undefined) => {
    close();
    if (!o) return;
    if (o.kind === "query") go(o.q);
    else if (o.kind === "notice") openPanel({ kind: "notice", key: o.key, feedId: null });
    else if (o.kind === "saved") navigate(`/saved/${o.id}`);
    else navigate("/advanced?q=" + encodeURIComponent(text.trim()));
  };

  const show = async (current: string) => {
    const q = current.trim();
    const mine = ++seq.current;
    const out: Option[] = [];
    if (!q) {
      for (const r of recentSearches()) out.push({ kind: "query", q: r, label: r, icon: "↺", group: "Recent" });
      out.push({ kind: "advanced", label: "Advanced search", icon: "⚙" });
    } else {
      out.push({ kind: "query", q, label: q, icon: "⌕", hint: "Search everything" });
      let s: SuggestResponse;
      try {
        s = await api<SuggestResponse>("/api/search/suggest?q=" + encodeURIComponent(q));
      } catch {
        return;
      }
      if (mine !== seq.current) return;
      const notice = (n: SuggestNotice, group: string) =>
        out.push({
          kind: "notice",
          key: n.key,
          label: n.title,
          sub: [n.solicitation_id, n.buyer, n.deadline ? dueText(n) : ""].filter(Boolean).join(" · "),
          icon: <Badge agency={n.agency} small />,
          group,
        });
      // A number jumps straight to its notice; otherwise filters beat title guesses for short prefixes.
      for (const n of s.ids) notice(n, "Solicitation numbers");
      for (const f of s.filters)
        out.push({
          kind: "query",
          q: `${f.name}:${f.value.includes(" ") ? '"' + f.value + '"' : f.value}`,
          label: f.label,
          sub: `${FACET_NAMES[f.name] ?? f.name} · ${f.count} open`,
          icon: "⊕",
          group: "Filter by",
        });
      for (const n of s.notices) notice(n, "Titles");
      for (const x of s.saved) out.push({ kind: "saved", id: x.id, label: x.name, sub: x.query, icon: "★", group: "Saved searches" });
      out.push({ kind: "advanced", label: "Advanced search", icon: "⚙" });
    }
    if (mine !== seq.current) return;
    setOptions(out);
    setOpen(true);
    setActive(q ? 0 : -1);
  };
  const schedule = (current: string, ms: number) => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => void show(current), ms);
  };

  let group: string | undefined;
  return (
    <form
      className="search"
      id="search-form"
      role="search"
      autoComplete="off"
      onSubmit={(e) => {
        e.preventDefault();
        const q = text.trim();
        const o = options[active];
        if (open && active >= 0 && o && !(o.kind === "query" && o.q === q)) {
          choose(o);
          return;
        }
        close();
        go(q, params.get("sort"));
      }}
    >
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
        <circle cx="7" cy="7" r="5" />
        <path d="m11 11 3.5 3.5" />
      </svg>
      <input
        id="q"
        ref={input}
        value={text}
        placeholder="Search opportunities, buyers, numbers…"
        aria-label="Search everything"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls="suggest"
        aria-activedescendant={open && active >= 0 ? `sg-${active}` : undefined}
        onChange={(e) => {
          setText(e.target.value);
          schedule(e.target.value, 120);
        }}
        onFocus={(e) => schedule(e.target.value, 60)}
        onBlur={() => window.setTimeout(close, 120)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            e.preventDefault();
            if (!open) {
              void show(text);
              return;
            }
            setActive((a) => Math.max(0, Math.min(options.length - 1, a + (e.key === "ArrowDown" ? 1 : -1))));
          } else if (e.key === "Escape") {
            if (open) {
              e.stopPropagation();
              e.nativeEvent.stopImmediatePropagation();
              close();
            } else input.current?.blur();
          }
        }}
      />
      <kbd>/</kbd>
      <div className="suggest" id="suggest" role="listbox" hidden={!open}>
        {open
          ? options.map((o, i) => {
              let head: ReactNode = null;
              if (o.group && o.group !== group) {
                group = o.group;
                head = <div className="sg-head">{group}</div>;
              } else if (!o.group) group = undefined;
              return (
                <Fragment key={i}>
                  {head}
                  <div
                    className="sg"
                    role="option"
                    id={`sg-${i}`}
                    aria-selected={i === active}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      choose(o);
                    }}
                  >
                    <span className="sg-ico">{o.icon}</span>
                    <span className="sg-main">
                      <span className="sg-label">{o.label}</span>
                      {o.sub ? <span className="sg-sub">{o.sub}</span> : null}
                    </span>
                    {"hint" in o && o.hint ? <span className="sg-hint">{o.hint} ↵</span> : null}
                  </div>
                </Fragment>
              );
            })
          : null}
      </div>
    </form>
  );
}
